import React, { useCallback, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { GenericListPage } from '../../components/generic';
import { usePayables, usePayablesWithStatusFilter, Payable } from './useData';
import { moduleConfig, getColumns, getToolbarIcon, getTableActions } from './config';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { MarkAsPaidModal } from './MarkAsPaidModal';
import { InvoiceIntakeDropZone } from './InvoiceIntakeDropZone';
import toast from 'react-hot-toast';

const List: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || 'pending';
  const [payableToMarkPaid, setPayableToMarkPaid] = useState<Payable | null>(null);
  const { mutate: refetchList } = usePayables();

  const onDuplicate = useCallback(async (item: Payable) => {
    try {
      const { createPayable } = await import('../../services/mutations');
      const payload = {
        payeeId: item.payeeId,
        invoiceDate: item.invoiceDate,
        dueDate: item.dueDate,
        amount: item.amount,
        notes: item.notes ? `${item.notes} (copy)` : undefined,
        categoryId: item.categoryId ?? undefined,
        subCategoryId: item.subCategoryId ?? undefined,
        status: 'pending',
      };
      const created = await createPayable(payload) as { id?: number };
      refetchList();
      const newId = created?.id;
      if (typeof newId === 'number') {
        toast.success('Payable duplicated');
        navigate(`/payables/${newId}/edit`);
      } else {
        toast.success('Payable duplicated');
      }
    } catch (err) {
      toast.error('Failed to duplicate payable');
      console.error(err);
    }
  }, [navigate, refetchList]);

  const listContext = { onMarkPaid: (item: Payable) => setPayableToMarkPaid(item), onDuplicate };

  const filterLinkClass = (active: boolean) =>
    cn(
      'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
      active
        ? 'bg-indigo-600 text-white shadow-sm'
        : cn(colors.text, colors.surfaceHover, 'hover:bg-gray-100 dark:hover:bg-gray-700'),
    );

  return (
    <>
      <div className={cn('flex flex-col h-full min-h-0', colors.background)}>
        <div className="flex-shrink-0 p-3 pb-0">
          <InvoiceIntakeDropZone />
        </div>
        <div className="flex-shrink-0 flex flex-wrap items-center gap-2 p-3 pb-2">
          <span className={cn('text-xs font-medium mr-1', colors.textMuted)}>Status</span>
          <div className={cn('inline-flex rounded-lg p-1 gap-0.5 border', colors.border, colors.surface)} role="group" aria-label="Filter by status">
            <Link to="/payables?status=pending" className={filterLinkClass(statusFilter === 'pending')} aria-pressed={statusFilter === 'pending'}>
              Unpaid
            </Link>
            <Link to="/payables?status=overdue" className={filterLinkClass(statusFilter === 'overdue')} aria-pressed={statusFilter === 'overdue'}>
              Overdue
            </Link>
            <Link to="/payables?status=paid" className={filterLinkClass(statusFilter === 'paid')} aria-pressed={statusFilter === 'paid'}>
              Paid
            </Link>
            <Link to="/payables?status=all" className={filterLinkClass(statusFilter === 'all')} aria-pressed={statusFilter === 'all'}>
              All
            </Link>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <GenericListPage<Payable>
            useData={usePayablesWithStatusFilter}
            config={{
              ...moduleConfig,
              defaultTableDensity: 'compact',
              getColumns: (formatDate, formatCurrency) => getColumns(formatDate, formatCurrency, colors),
              getToolbarIcon,
              getTableActions: (item, onDelete, c, _ctx, opts) => getTableActions(item, onDelete, colors, listContext, opts),
            }}
          />
        </div>
      </div>
      {payableToMarkPaid != null && (
        <MarkAsPaidModal
          payable={payableToMarkPaid}
          onClose={() => setPayableToMarkPaid(null)}
          onSuccess={() => refetchList()}
        />
      )}
    </>
  );
};

export default List;
